import { useEffect, useRef } from 'react';
import { Message } from './messages/Message';
import { LoadingMessage } from './messages/LoadingMessage';
import { chatApi } from '../../../shared/api/chatApi';
import type { Database } from '../../../shared/lib/database.types';


type MessageData = Database['public']['Tables']['messages']['Row'];

interface MessageListProps {
  messages: MessageData[];
  isLoading: boolean;
  isGeneratingQuestions: boolean;
  streamingMessage: string | null;
  streamingQuestions: string | null;
  streamingError: boolean;
  isMessageLoading: boolean;
  onRetry: (threadId: string, chatId: string) => void;
  onTellMeMore: (verse: { book: string; chapter: string; verse: string }) => void;
  onFollowUpQuestion: (question: string) => void;
  chatId: string;
  threadId: string;
}

export function MessageList({
  messages,
  isLoading,
  isGeneratingQuestions,
  streamingMessage,
  streamingQuestions,
  streamingError,
  isMessageLoading,
  onRetry,
  onTellMeMore,
  onFollowUpQuestion,
  chatId,
  threadId,
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const shouldScroll = (messages?.length ?? 0) > 0 || streamingMessage !== null || streamingQuestions !== null;
    
    if (shouldScroll) {
      const timeoutId = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [messages?.length, streamingMessage, streamingQuestions]);

  return (
    <><div className="space-y-[50px]">



        {messages.map((message, index) => {
          const isFirstUserMessage = message.role === 'user' &&
            messages.findIndex(m => m.role === 'user') === index;

          return (
            <Message
              key={message.id}
              content={message.content}
              role={message.role}
              onTellMeMore={onTellMeMore}
              onFollowUpQuestion={onFollowUpQuestion}
              isFirst={isFirstUserMessage} />
          );
        })}
        {isLoading && !streamingMessage && messages.length === 0 && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        )}
        {streamingMessage !== null && (
          <Message
            content={streamingMessage}
            role="assistant"
            isStreaming={true}
            hasError={streamingError}
            onRetry={() => onRetry(threadId, chatId)}
            onTellMeMore={onTellMeMore}
            onFollowUpQuestion={onFollowUpQuestion} />
        )}
        {isMessageLoading && !streamingMessage && (
          <LoadingMessage />
        )}
        {streamingQuestions !== null && (
          <Message
            content={streamingQuestions}
            role="questions"
            isStreaming={true}
            onTellMeMore={onTellMeMore}
            onFollowUpQuestion={onFollowUpQuestion} />
        )}
        <div ref={messagesEndRef} />
      </div></>
  );
} 